const moment = require('moment-timezone')

function browserTimezone () {
  if (typeof Intl === 'undefined' || !Intl.DateTimeFormat) return 'UTC'
  const options = Intl.DateTimeFormat().resolvedOptions()
  return options && options.timeZone ? options.timeZone : 'UTC'
}

function tripProfile (trip) {
  if (!trip) return null
  if (typeof trip.transportationProfile === 'function') return trip.transportationProfile()
  return trip.transportationProfile || null
}

function profileLabel (profile) {
  if (!profile) return ''
  return profile.licensePlateNumber || profile.id || ''
}

function resolveTransferTicketMode (selectedTickets) {
  const tickets = Array.isArray(selectedTickets) ? selectedTickets.filter(Boolean) : []

  if (!tickets.length) {
    return { mode: 'bulk', ticket: null }
  }

  if (tickets.length > 1) {
    return {
      error: 'Select only one ticket to transfer, or clear the selection to transfer by agent and time range.'
    }
  }

  return { mode: 'single', ticket: tickets[0] }
}

function resolveFromTransportationProfile (trips) {
  const list = Array.isArray(trips) ? trips : []
  const profiles = []
  const seen = {}
  let missing = false

  for (let i = 0; i < list.length; i++) {
    const profile = tripProfile(list[i])
    if (!profile || !profile.id) {
      missing = true
      continue
    }
    if (seen[profile.id]) continue
    seen[profile.id] = true
    profiles.push(profile)
  }

  const plates = profiles.map(profileLabel).filter(Boolean)

  if (profiles.length === 1 && !missing) {
    return { profile: profiles[0], plates: plates }
  }

  return { profile: null, plates: plates }
}

function emptyTransferTicket (mode, ticket) {
  return {
    mode: mode || 'bulk',
    ticket: ticket || null,
    fromProfile: null,
    toProfile: null,
    dateFrom: null,
    dateTo: null,
    agent: null,
    mixedPlates: []
  }
}

function dayKey (value, timezone) {
  if (!value) return ''
  const parsed = moment(value)
  if (!parsed.isValid()) return ''
  return parsed.tz(timezone || browserTimezone()).format('YYYY-MM-DD')
}

function startOfDay (value, timezone) {
  const zone = timezone || browserTimezone()
  const day = dayKey(value, zone)
  if (!day) return null
  return moment.tz(day + ' 00:00', 'YYYY-MM-DD HH:mm', zone).toDate()
}

function endOfDay (value, timezone) {
  const zone = timezone || browserTimezone()
  const day = dayKey(value, zone)
  if (!day) return null
  return moment.tz(day + ' 23:59', 'YYYY-MM-DD HH:mm', zone).toDate()
}

function reconcileTransferDateTo (dateFrom, dateTo, timezone) {
  if (!dateFrom || !dateTo) return dateTo || null
  if (dayKey(dateFrom, timezone) !== dayKey(dateTo, timezone)) return null
  if (new Date(dateTo).getTime() <= new Date(dateFrom).getTime()) return null
  return dateTo
}

function getTransferDateBounds (dateFrom, dateTo, timezone) {
  return {
    fromMin: dateTo ? startOfDay(dateTo, timezone) : null,
    fromMax: dateTo || null,
    toMin: dateFrom || null,
    toMax: dateFrom ? endOfDay(dateFrom, timezone) : null
  }
}

function hasAgentUserId (agent) {
  if (!agent) return false
  return agent.stUserId !== null && agent.stUserId !== undefined && String(agent.stUserId).trim() !== ''
}

function rejectSameTransferVehicle (nextProfile, otherProfile) {
  if (!nextProfile || !otherProfile) return nextProfile || null
  if (nextProfile.id && otherProfile.id && nextProfile.id === otherProfile.id) return null
  return nextProfile
}

function canSaveTransferTicket (form) {
  if (!form || !form.fromProfile || !form.fromProfile.id) return false
  if (!form.toProfile || !form.toProfile.id) return false
  if (form.fromProfile.id === form.toProfile.id) return false
  if (form.mode === 'single') return Boolean(form.ticket && form.ticket.id)
  if (!form.dateFrom || !form.dateTo || !hasAgentUserId(form.agent)) return false
  return reconcileTransferDateTo(form.dateFrom, form.dateTo) === form.dateTo
}

function buildTransferTicketRequest (form) {
  const body = {
    fromTransportationProfileId: form.fromProfile.id,
    toTransportationProfileId: form.toProfile.id
  }

  if (form.mode === 'single') {
    body.passengerTransactionLogId = form.ticket.id
    return body
  }

  body.dateFrom = form.dateFrom instanceof Date ? form.dateFrom.toISOString() : form.dateFrom
  body.dateTo = form.dateTo instanceof Date ? form.dateTo.toISOString() : form.dateTo
  body.requestedByStUserId = String(form.agent.stUserId).trim()
  return body
}

function buildTransferConfirmMessage (form) {
  const fromPlate = profileLabel(form.fromProfile) || 'the selected vehicle'
  const toPlate = profileLabel(form.toProfile) || 'the selected vehicle'

  if (form.mode === 'single') {
    const ticketId = form.ticket && form.ticket.parsedId ? form.ticket.parsedId : 'this ticket'
    return 'Transfer ' + ticketId + ' from ' + fromPlate + ' to ' + toPlate + '?'
  }

  const agentName = form.agent && form.agent.fullName ? form.agent.fullName : 'the selected agent'
  return 'Transfer tickets from ' + fromPlate + ' to ' + toPlate + ' for ' + agentName + ' in this time range?'
}

function transferSuccessMessage (updatedTripCount, toProfile) {
  const count = Number(updatedTripCount) || 0
  const plate = profileLabel(toProfile) || 'the selected vehicle'
  const noun = count === 1 ? 'trip' : 'trips'
  return 'Transferred ' + count + ' ' + noun + ' to ' + plate + '.'
}

module.exports = {
  resolveTransferTicketMode: resolveTransferTicketMode,
  resolveFromTransportationProfile: resolveFromTransportationProfile,
  emptyTransferTicket: emptyTransferTicket,
  reconcileTransferDateTo: reconcileTransferDateTo,
  getTransferDateBounds: getTransferDateBounds,
  rejectSameTransferVehicle: rejectSameTransferVehicle,
  canSaveTransferTicket: canSaveTransferTicket,
  buildTransferTicketRequest: buildTransferTicketRequest,
  buildTransferConfirmMessage: buildTransferConfirmMessage,
  transferSuccessMessage: transferSuccessMessage
}
